<template>
  <aside data-test="navbar-employee-desktop-project">
    <div class="flex h-full min-h-0 w-full flex-col gap-2 rounded-r-2xl bg-white md:w-75">
      <div class="flex h-50 shrink-0 items-center justify-center">
        <div class="flex flex-col items-center gap-3">
          <avatar
            :format="current_client.attributes.picture_logo_format"
            :src="current_client.attributes.picture_logo_url_s"
            :username="current_client.attributes.name"
            class="cursor-pointer"
            size="90"
          />
          <div
            v-if="has_multiple_profiles"
            class="flex items-center gap-2"
          >
            <div
              class="flex cursor-pointer items-center gap-2"
              @click="togglePopover($event)"
            >
              <p class="text-center">{{ $t('navbar.topnavbar.selectTeam') }}</p>
              <FontAwesomeIcon
                :icon="['fas', 'chevron-down']"
                size="sm"
              />
            </div>
            <Popover ref="select_tenant_popover">
              <SelectTenant />
            </Popover>
          </div>
        </div>
      </div>

      <AppLink
        :class="{
          'text-primary-600 font-semibold': route.name.startsWith(ROUTES_PARTNERS.CLIENT_DASHBOARD),
        }"
        :to="{ name: ROUTES_PARTNERS.CLIENT_DASHBOARD }"
        class="flex shrink-0 items-center gap-2 px-5 text-sm"
        @click="emit('close')"
      >
        <div class="flex w-7.5 shrink-0 items-center justify-center">
          <FontAwesomeIcon
            :icon="['far', 'grid-horizontal']"
            size="lg"
          />
        </div>
        <span class="min-w-0 flex-1 truncate">{{ $t('client.labels.dashboard') }}</span>
      </AppLink>

      <div class="mb-3 flex shrink-0 px-5">
        <IconField>
          <InputIcon class="pi pi-search" />
          <InputText
            v-model="searchQuery"
            :placeholder="$t('project.labels.searchProjects')"
            size="small"
          />
        </IconField>
      </div>

      <div class="ml-3 flex min-h-0 flex-1 flex-col overflow-y-auto">
        <NavbarProjectItem
          v-for="project in filteredProjects"
          :key="project.id"
          :is_active="activeProjectId === project.id"
          :project="project"
          @close="emit('close')"
          @toggle="handleProjectToggle"
        />
        <div
          v-if="filteredProjects.length === 0"
          class="text-sm text-gray-500"
        >
          {{ $t('project.labels.noProjects') }}
        </div>
      </div>

      <div class="mt-2 flex shrink-0 flex-col justify-start gap-2">
        <div
          v-tooltip.bottom="$t('client.labels.contactZiggu')"
          class="hover:bg-surface-100 mx-4 mb-0 flex cursor-pointer gap-2 rounded-md p-2 text-sm font-medium sm:justify-start"
          @click="openIntercom"
        >
          <FontAwesomeIcon
            class="inline shrink-0 sm:hidden"
            :icon="['far', 'comment']"
            size="lg"
          />
          <span>{{ $t('client.labels.chatWithUs') }}</span>
        </div>
        <HelpSidebar />
      </div>

      <div class="shrink-0">
        <ProfileDropdown
          #reference
          :is_mobile="true"
          @close-sidebar="emit('close')"
        />
      </div>
    </div>
  </aside>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { ROUTES_PARTNERS } from '@/app/data/route_constants';
import NavbarProjectItem from '@/app/_partners/components/navbar/NavbarProjectItem.vue';
import ProfileDropdown from '@/app/shared_components/navbar/TheProfileDropdown.vue';
import HelpSidebar from '@/app/shared_components/navbar/TheHelpSidebar.vue';
import { useRoute } from 'vue-router';
import SelectTenant from '@/app/shared_components/SelectTenant.vue';

const store = useStore();
const route = useRoute();

const emit = defineEmits(['close']);

const current_client = computed(() => store.getters.current_client);
const current_profile_true = computed(() => store.getters.current_profile_true);

const has_multiple_profiles = computed(() => current_profile_true.value.attributes.profiles_count > 1);

const projects = ref([]);
const searchQuery = ref('');

const filteredProjects = computed(() => {
  if (!searchQuery.value.trim()) {
    return projects.value;
  }

  return _.filter(projects.value, (project) =>
    _.includes(project.attributes.name.toLowerCase(), searchQuery.value.trim().toLowerCase()),
  );
});

const activeProjectId = ref(null);

const handleProjectToggle = (projectId) => {
  if (activeProjectId.value === projectId) {
    activeProjectId.value = null;
  } else {
    activeProjectId.value = projectId;
  }
};

const select_tenant_popover = ref(null);

const togglePopover = (event) => {
  select_tenant_popover.value.toggle(event);
};

const openIntercom = () => {
  window.Intercom?.('show');
};

onMounted(async () => {
  projects.value = await store.dispatch('FETCH_PROJECTS');

  const urlProjectId = route.query.project_id || route.params.project_id;
  if (urlProjectId) {
    activeProjectId.value = urlProjectId;
  }
});
</script>
